import * as React from 'react'
import { clsx } from 'clsx'
import { Slot } from '@radix-ui/react-slot'

import { Label } from '../Label'
import type { LabelProps } from '../Label'
import { VisuallyHidden } from '../VisuallyHidden'
import { TriangleAlertIcon } from '../icons'
import { useMergeRefs } from '../../hooks/useMergeRefs'
import { FormControlContext, mergeIds } from './useFormControlProps'

import styles from './Form.module.css'
import iconSlotStyles from '../../styles/atoms/icon-slot.module.css'

/**
 * Form
 */
type FormProps = React.ComponentPropsWithoutRef<'form'> & {
  /**
   * Name of the form
   */
  name?: string
  children: React.ReactNode
  /**
   * Func which will be trigged when the form is submitted
   */
  onSubmit?: React.FormEventHandler<HTMLFormElement>
}

/**
 * Form renders a native form boundary and exposes compound field primitives for
 * accessible labels, descriptions, errors, and control state.
 */
const Form = React.forwardRef<React.ElementRef<'form'>, FormProps>(
  ({ name, children, onSubmit, ...props }: FormProps, ref) => {
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault()
      onSubmit?.(event)
    }

    return (
      <form name={name} onSubmit={handleSubmit} ref={ref} {...props}>
        {children}
      </form>
    )
  },
)

Form.displayName = 'Form'

/**
 * Form Field Context
 */
interface FormFieldContextValue {
  controlId: string
  labelId?: string
  controlRef: React.MutableRefObject<HTMLElement | null>
  name: string
  invalid: boolean
  required: boolean
  descriptionIds: string[]
  errorIds: string[]
  registerLabel: (id: string) => void
  unregisterLabel: (id: string) => void
  registerDescription: (id: string) => void
  unregisterDescription: (id: string) => void
  registerError: (id: string) => void
  unregisterError: (id: string) => void
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null)

const useFormField = (): FormFieldContextValue => {
  const context = React.useContext(FormFieldContext)

  if (!context) {
    throw new Error('useFormField must be used within a Form.Field')
  }

  return context
}

const addUniqueId = (ids: string[], id: string): string[] => {
  if (ids.includes(id)) return ids
  return [...ids, id]
}

const removeId = (ids: string[], id: string): string[] =>
  ids.filter((currentId) => currentId !== id)

const hasRenderableChildren = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).some((child) => {
    if (typeof child === 'string') return child.trim() !== ''
    return true
  })

const useFieldMessageRegistration = ({
  id,
  shouldRegister,
  register,
  unregister,
}: {
  id: string
  shouldRegister: boolean
  register: (id: string) => void
  unregister: (id: string) => void
}): void => {
  React.useEffect(() => {
    if (!shouldRegister) return undefined

    register(id)
    return (): void => unregister(id)
  }, [id, register, shouldRegister, unregister])
}

/**
 * Form Field
 */
type FormFieldProps = React.ComponentPropsWithoutRef<'div'> & {
  /**
   * Name of the form field
   */
  name: string
  /**
   * Stable id for the actual form control. The regular `id` prop remains the wrapper id.
   */
  controlId?: string
  /**
   * Whether the field is currently invalid.
   */
  invalid?: boolean
  /**
   * Whether the field is required. Consumers still own native `required` validation.
   */
  required?: boolean
  className?: string
}

const FormField = React.forwardRef<React.ElementRef<'div'>, FormFieldProps>(
  (
    {
      className,
      name,
      controlId: controlIdProp,
      invalid = false,
      required = false,
      ...props
    },
    ref,
  ) => {
    const generatedControlId = React.useId()
    const controlId = controlIdProp ?? generatedControlId
    const [labelIds, setLabelIds] = React.useState<string[]>([])
    const labelId = mergeIds(...labelIds)
    const controlRef = React.useRef<HTMLElement | null>(null)
    const [descriptionIds, setDescriptionIds] = React.useState<string[]>([])
    const [errorIds, setErrorIds] = React.useState<string[]>([])

    const registerLabel = React.useCallback((id: string) => {
      setLabelIds((ids) => addUniqueId(ids, id))
    }, [])

    const unregisterLabel = React.useCallback((id: string) => {
      setLabelIds((ids) => removeId(ids, id))
    }, [])

    const registerDescription = React.useCallback((id: string) => {
      setDescriptionIds((ids) => addUniqueId(ids, id))
    }, [])

    const unregisterDescription = React.useCallback((id: string) => {
      setDescriptionIds((ids) => removeId(ids, id))
    }, [])

    const registerError = React.useCallback((id: string) => {
      setErrorIds((ids) => addUniqueId(ids, id))
    }, [])

    const unregisterError = React.useCallback((id: string) => {
      setErrorIds((ids) => removeId(ids, id))
    }, [])

    const context = React.useMemo<FormFieldContextValue>(
      () => ({
        controlId,
        labelId,
        controlRef,
        name,
        invalid,
        required,
        descriptionIds,
        errorIds,
        registerLabel,
        unregisterLabel,
        registerDescription,
        unregisterDescription,
        registerError,
        unregisterError,
      }),
      [
        controlId,
        labelId,
        descriptionIds,
        errorIds,
        invalid,
        name,
        registerDescription,
        registerError,
        registerLabel,
        required,
        unregisterDescription,
        unregisterError,
        unregisterLabel,
      ],
    )

    return (
      <FormFieldContext.Provider value={context}>
        <div
          ref={ref}
          className={clsx(styles.field, className)}
          data-invalid={invalid ? true : undefined}
          data-required={required ? true : undefined}
          {...props}
        />
      </FormFieldContext.Provider>
    )
  },
)

FormField.displayName = 'Form.Field'

/**
 * Form Label
 */
const FormLabel = React.forwardRef<React.ElementRef<'label'>, LabelProps>(
  ({ id: idProp, onClick, ...props }, ref) => {
    const { controlId, controlRef, registerLabel, unregisterLabel } =
      useFormField()
    const generatedLabelId = React.useId()
    const id = idProp ?? generatedLabelId

    useFieldMessageRegistration({
      id,
      shouldRegister: true,
      register: registerLabel,
      unregister: unregisterLabel,
    })

    const handleClick = (event: React.MouseEvent<HTMLLabelElement>): void => {
      onClick?.(event)

      const control = controlRef.current
      const target = event.target
      if (
        target instanceof Element &&
        target.closest(
          'a[href], button, input, select, textarea, [contenteditable="true"]',
        )
      )
        return

      // htmlFor does not focus contenteditable elements, so the label focuses them directly.
      if (
        !event.defaultPrevented &&
        control &&
        (control.isContentEditable ||
          control.getAttribute('contenteditable') === 'true')
      ) {
        control.focus()
      }
    }

    return (
      <Label
        ref={ref}
        id={id}
        htmlFor={controlId}
        onClick={handleClick}
        {...props}
      />
    )
  },
)

FormLabel.displayName = 'Form.Label'

type FormControlDataProps = {
  'data-invalid'?: boolean
  'data-required'?: boolean
}

type FormControlProps = Omit<
  React.ComponentPropsWithoutRef<typeof Slot>,
  'id'
> &
  FormControlDataProps & {
    name?: string
  }

/**
 * Form Control
 */
const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  FormControlProps
>(
  (
    {
      children,
      name: controlName,
      'aria-describedby': ariaDescribedBy,
      'aria-labelledby': ariaLabelledBy,
      'aria-invalid': ariaInvalid,
      'aria-required': ariaRequired,
      'data-invalid': dataInvalid,
      'data-required': dataRequired,
      ...props
    },
    ref,
  ) => {
    const {
      controlId,
      controlRef,
      labelId,
      name,
      invalid,
      required,
      descriptionIds,
      errorIds,
    } = useFormField()
    const describedBy = mergeIds(
      ariaDescribedBy,
      ...descriptionIds,
      ...errorIds,
    )
    const mergedRef = useMergeRefs(ref, controlRef)
    const context = React.useMemo(
      () => ({ id: controlId, labelId, describedBy }),
      [controlId, labelId, describedBy],
    )

    const controlProps = {
      ...props,
      ref: mergedRef,
      id: controlId,
      name: controlName ?? name,
      'aria-describedby': describedBy,
      'aria-labelledby': ariaLabelledBy,
      'aria-invalid': ariaInvalid ?? (invalid ? true : undefined),
      'aria-required': ariaRequired ?? (required ? true : undefined),
      'data-invalid': dataInvalid ?? (invalid ? true : undefined),
      'data-required': dataRequired ?? (required ? true : undefined),
    }

    return (
      <FormControlContext.Provider value={context}>
        <Slot {...controlProps}>{children}</Slot>
      </FormControlContext.Provider>
    )
  },
)

FormControl.displayName = 'Form.Control'

type FormDescriptionProps = React.ComponentPropsWithoutRef<'p'>

/**
 * Form Description
 */
const FormDescription = React.forwardRef<
  React.ElementRef<'p'>,
  FormDescriptionProps
>(({ children, id: idProp, className, ...props }, ref) => {
  const { registerDescription, unregisterDescription } = useFormField()
  const generatedId = React.useId()
  const id = idProp ?? generatedId
  const shouldRender = hasRenderableChildren(children)

  useFieldMessageRegistration({
    id,
    shouldRegister: shouldRender,
    register: registerDescription,
    unregister: unregisterDescription,
  })

  if (!shouldRender) return null

  return (
    <p
      ref={ref}
      id={id}
      className={clsx(styles.description, className)}
      {...props}
    >
      {children}
    </p>
  )
})

FormDescription.displayName = 'Form.Description'

type FormErrorProps = React.ComponentPropsWithoutRef<'p'>

/**
 * Form Error
 */
const FormError = React.forwardRef<React.ElementRef<'p'>, FormErrorProps>(
  ({ children, id: idProp, className, ...props }, ref) => {
    const { registerError, unregisterError } = useFormField()
    const generatedId = React.useId()
    const id = idProp ?? generatedId
    const shouldRender = hasRenderableChildren(children)

    useFieldMessageRegistration({
      id,
      shouldRegister: shouldRender,
      register: registerError,
      unregister: unregisterError,
    })

    if (!shouldRender) return null

    return (
      <p ref={ref} id={id} className={clsx(styles.error, className)} {...props}>
        <span className={iconSlotStyles.firstLine}>
          <TriangleAlertIcon size="xsmall" />
        </span>
        <span className={styles.errorContent}>{children}</span>
      </p>
    )
  },
)

FormError.displayName = 'Form.Error'

type FormRequiredIndicatorProps = React.ComponentPropsWithoutRef<'span'> & {
  /**
   * Optional text announced with the label. Required semantics come from
   * `Form.Control` setting `aria-required` when the field is required.
   */
  label?: React.ReactNode
}

/**
 * Form Required Indicator
 */
const FormRequiredIndicator = React.forwardRef<
  React.ElementRef<'span'>,
  FormRequiredIndicatorProps
>(({ children = '*', className, label = null, ...props }, ref) => {
  const { required } = useFormField()

  if (!required) return null

  return (
    <span
      ref={ref}
      className={clsx(styles.requiredIndicator, className)}
      {...props}
    >
      <span aria-hidden="true">{children}</span>
      {label ? <VisuallyHidden>{label}</VisuallyHidden> : null}
    </span>
  )
})

FormRequiredIndicator.displayName = 'Form.RequiredIndicator'

const FormObject = Object.assign(Form, {
  Field: FormField,
  Label: FormLabel,
  Control: FormControl,
  Description: FormDescription,
  Error: FormError,
  RequiredIndicator: FormRequiredIndicator,
})

export { FormObject as Form }
export type {
  FormProps,
  FormFieldProps,
  FormControlProps,
  FormDescriptionProps,
  FormErrorProps,
  FormRequiredIndicatorProps,
}
